import { MOTOR_COVER_COMPREHENSIVE, MOTOR_COVER_TPO } from '@/constants/motorPrivate';

const RENEWAL_WINDOW_DAYS = 90;
export const RENEWAL_LAPSE_LIMIT_MONTHS = 2;

export function policyToSlug(policyNo) {
  if (!policyNo) return '';
  return String(policyNo).replace(/\//g, '-');
}

export function slugToPolicyNo(slug) {
  if (!slug) return '';
  return String(slug).replace(/-/g, '/');
}

/** Motor private / commercial renewals only — this portal journey is not for PA, travel, etc. */
export function isMotorRenewal(renewal) {
  if (!renewal) return false;
  // GIS batch detail / list may send proMotoVerfy — authoritative when present
  if (renewal.proMotoVerfy != null && String(renewal.proMotoVerfy).trim() !== '') {
    return String(renewal.proMotoVerfy).trim().toUpperCase() === 'Y';
  }

  const product = String(renewal.product || renewal.productGroup || renewal.coverType || '').toUpperCase();
  if (/\bMOTOR\b/.test(product) || product.includes('BIMA BAMBA')) return true;

  const policyNo = String(renewal.policyNo || '').replace(/\s+/g, '');
  // Pacis motor policy numbers commonly look like 10/700/... or 010/070/...
  if (/^0?10\/0?7\d*\//i.test(policyNo) || /^0?10-0?7/i.test(policyNo)) return true;

  const risks = Array.isArray(renewal.risks) ? renewal.risks : [];
  return risks.some((risk) => {
    const cover = String(risk?.coverType || '').toUpperCase();
    if (cover.includes('COMPREHENSIVE') || cover.includes('THIRD PARTY') || cover === 'TPO') return true;
    // Kenyan plate pattern in risk id/desc
    return /^K[A-Z]{2}\s?\d{1,3}[A-Z]{0,3}$/i.test(String(risk?.riskId || risk?.riskDesc || '').trim());
  });
}

/**
 * Merge GIS GET /agent/renewals/{batchNo} into the list/session renewal for confirm UI.
 */
export function mergeGisRenewalBatchDetail(base, detail) {
  if (!detail || typeof detail !== 'object') return base || null;
  const currentPremium = base?.premium;
  const merged = { ...(base || {}), ...detail };

  const future = Number(detail.futurePremium ?? detail.fap ?? detail.cf1);
  if (Number.isFinite(future) && future > 0) {
    merged.futurePremium = future;
  } else if (detail.premium != null && merged.futurePremium == null) {
    merged.futurePremium = detail.premium;
  }

  // Keep list "current" premium when present; confirm UI uses futurePremium via renewalPremium()
  if (currentPremium != null && currentPremium !== '') {
    merged.premium = currentPremium;
  } else if (detail.premium != null) {
    merged.premium = detail.premium;
  } else if (Number.isFinite(future) && future > 0) {
    merged.premium = future;
  }

  if (detail.product != null) merged.product = detail.product;
  if (detail.renewalDate != null) merged.renewalDate = detail.renewalDate;
  if (detail.sumInsured != null) merged.sumInsured = detail.sumInsured;
  if (Array.isArray(detail.risks) && detail.risks.length) merged.risks = detail.risks;
  if (detail.proMotoVerfy != null) merged.proMotoVerfy = detail.proMotoVerfy;

  if (detail.policyNo) merged.policyNo = detail.policyNo;
  const batch = detail.effectiveBatchNo ?? detail.batchNo ?? base?.effectiveBatchNo ?? base?.batchNo;
  if (batch != null) {
    merged.effectiveBatchNo = batch;
    merged.batchNo = batch;
  }

  if (detail.clientName || detail.clntName) {
    merged.clientName = detail.clientName || detail.clntName;
  }

  return merged;
}

export function saveRenewalToSession(renewal, renewalType = 'pending') {
  if (!renewal?.policyNo) return;
  const slug = policyToSlug(renewal.policyNo);
  try {
    sessionStorage.setItem(`agentRenewal_${slug}`, JSON.stringify(renewal));
    sessionStorage.setItem(`agentRenewalMeta_${slug}`, JSON.stringify({ renewalType }));
  } catch {
    /* ignore quota errors */
  }
}

/** Stores the renewal for the shared renew screen and returns the customer route to it. */
export function customerRenewalLocation(renewal, days = null) {
  saveRenewalToSession(renewal, days != null && days < 0 ? 'lapsed' : 'pending');
  const batch = renewal?.effectiveBatchNo || renewal?.batchNo;
  return {
    name: 'CustomerRenewPolicy',
    params: { policyNo: policyToSlug(renewal?.policyNo) },
    query: batch ? { batch: String(batch) } : {},
  };
}

/** Customer route to the renew screen from a My Policies record (renewal is resolved on that screen). */
export function customerPolicyRenewalLocation(policy) {
  return {
    name: 'CustomerRenewPolicy',
    params: { policyNo: policyToSlug(policy?.policyNo) },
    query: policy?.batchNo ? { batch: String(policy.batchNo) } : {},
  };
}

export function loadRenewalFromSession(slug) {
  if (!slug) return { renewal: null, renewalType: 'pending' };
  try {
    const raw = sessionStorage.getItem(`agentRenewal_${slug}`);
    const metaRaw = sessionStorage.getItem(`agentRenewalMeta_${slug}`);
    const renewal = raw ? JSON.parse(raw) : null;
    const meta = metaRaw ? JSON.parse(metaRaw) : {};
    return {
      renewal: renewal?.policyNo ? renewal : null,
      renewalType: meta.renewalType === 'lapsed' ? 'lapsed' : 'pending',
    };
  } catch {
    return { renewal: null, renewalType: 'pending' };
  }
}

export function saveRenewalJourneyProgress(slug, state) {
  if (!slug || !state) return;
  try {
    sessionStorage.setItem(`agentRenewalProgress_${slug}`, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

export function loadRenewalJourneyProgress(slug) {
  if (!slug) return null;
  try {
    const raw = sessionStorage.getItem(`agentRenewalProgress_${slug}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearRenewalJourneyProgress(slug) {
  if (!slug) return;
  try {
    sessionStorage.removeItem(`agentRenewalProgress_${slug}`);
  } catch {
    /* ignore */
  }
}

function isPaymentInFlight(raw) {
  try {
    const state = JSON.parse(raw);
    return state?.step === 'payment' && !!state?.paymentId;
  } catch {
    return false;
  }
}

/** Keeps journeys with an STK/paybill payment started so the agent can still confirm it. */
export function clearAllRenewalJourneyProgress() {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith('agentRenewalProgress_'))
      .filter((key) => !isPaymentInFlight(sessionStorage.getItem(key)))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

/**
 * Policies in Upcoming/Lapsed lists are already filtered by GIS (90-day window).
 * Lapsed tab policies are always renewable; upcoming within the renewal window.
 * Non-motor products are never renewable in this portal.
 */
function localDay(value) {
  if (value == null || value === '') return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
  const d = iso ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])) : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

function monthsBefore(date, months) {
  const target = new Date(date.getFullYear(), date.getMonth() - months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(date.getDate(), lastDay));
  return target;
}

/** True when the cover ended more than RENEWAL_LAPSE_LIMIT_MONTHS ago; those need a new quote instead. */
export function isLapsedBeyondRenewal(renewal, today = new Date()) {
  const renewalDay = localDay(renewal?.renewalDate);
  if (!renewalDay) return false;
  return renewalDay < monthsBefore(localDay(today), RENEWAL_LAPSE_LIMIT_MONTHS);
}

/**
 * Start date GIS gives the renewed cover. Mirrors quotations-api PolicyService.resolveEffectiveRenewalWef:
 * the renewal date when it is still ahead, otherwise the day the paid renewal is issued (today).
 */
export function effectiveRenewalStart(renewalDate, today = new Date()) {
  const todayDay = localDay(today);
  const renewalDay = localDay(renewalDate);
  return renewalDay && renewalDay > todayDay ? renewalDay : todayDay;
}

export function canRenewNow(renewal, renewalType = 'pending') {
  if (!isMotorRenewal(renewal)) return false;
  if (!renewal?.policyNo) return false;
  if (isLapsedBeyondRenewal(renewal)) return false;
  if (renewalType === 'lapsed') return true;
  if (!renewal.renewalDate) return true;
  const expiry = new Date(renewal.renewalDate);
  if (Number.isNaN(expiry.getTime())) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const windowEnd = new Date(today);
  windowEnd.setDate(windowEnd.getDate() + RENEWAL_WINDOW_DAYS);
  return expiry <= windowEnd;
}

export function formatRenewalDate(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return val;
  }
}

export function formatRenewalCurrency(value) {
  if (value == null || value === '') return 'Ksh 0';
  return `Ksh ${Number(value).toLocaleString()}`;
}

export function renewalPremium(renewal) {
  const future = Number(renewal?.futurePremium ?? renewal?.fap ?? renewal?.cf1);
  if (Number.isFinite(future) && future > 0) return future;
  return Number(renewal?.premium) || 0;
}

export function renewalPremiumLabel(renewal) {
  const future = Number(renewal?.futurePremium ?? renewal?.fap ?? renewal?.cf1);
  if (Number.isFinite(future) && future > 0) return 'Renewal premium';
  return 'Listed premium';
}

export function renewalCoverTypeId(renewal) {
  const explicit = Number(renewal?.coverTypeID || renewal?.coverCode || renewal?.covertypeId);
  if (explicit === MOTOR_COVER_COMPREHENSIVE || explicit === MOTOR_COVER_TPO) return explicit;
  const label = String(renewal?.risks?.[0]?.coverType || renewal?.product || '').toUpperCase();
  if (label.includes('THIRD') || label.includes('TPO') || label === 'TP') return MOTOR_COVER_TPO;
  return MOTOR_COVER_COMPREHENSIVE;
}

export function isComprehensiveRenewal(renewal) {
  return renewalCoverTypeId(renewal) === MOTOR_COVER_COMPREHENSIVE;
}

export function renewalPaymentTypeCode(paymentPlan) {
  return paymentPlan === 'installments' ? 'MONTHLY' : 'YEARLY';
}

export function renewalAmountDueNow(totalPremium, paymentPlan, isComprehensive = true) {
  const total = Number(totalPremium) || 0;
  if (isComprehensive && paymentPlan === 'installments') return Math.round(total * 0.4);
  return total;
}

export function renewalPaymentReference(renewal, risks = [], batchId = null) {
  const risk = Array.isArray(risks) ? risks[0] : null;
  const candidate = String(risk?.riskId || risk?.riskDesc || '').trim();
  if (candidate && candidate !== '—') {
    return candidate.replace(/\s+/g, '').toUpperCase();
  }
  const policy = String(renewal?.policyNo || '').replace(/\s+/g, '').replace(/\//g, '').toUpperCase();
  if (policy) return policy.slice(0, 12);
  return batchId ? String(batchId) : '';
}
